import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { QuadrantDataItemProps } from "./ChartTypes";
import { AssetEntityInspectorContent } from "@pages/homepage/asset-auto-discovery/AssetEntityInspector";
// import EntityInspector from "@pages/homepage/ddr-asset-auto-discovery/EntityInspector";
import UserInvestigator from "../UserInvestigator";
// import DDRUserInvestigator from "../ddr/user/DDRUserInvestigator";
import { isFeatureAllowed, ALL_KEYS } from "@utils/licenseUtils";

interface QuadChartProps {
  parentid: string;
  data: Array<QuadrantDataItemProps>;
  XLabel?: string;
  YLabel?: string;
  handleClick?: (label: string) => void;
}

const QuadrantChart: React.FC<QuadChartProps> = ({
  parentid,
  data,
  XLabel,
  YLabel,
  handleClick = () => {},
}) => {
  const scale = window.innerWidth / 1920;
  const svgRef = useRef(null);
  const parentRef = useRef(null);
  const infoRef = useRef(null);
  const [macAddress, setMacAddress] = React.useState("");
  const [ipAddress, setIpAddress] = React.useState("");
  const [userName, setUserName] = React.useState("");
  const [entityCount, setEntityCount] = React.useState(0);
  const [userCount, setUserCount] = React.useState(0);

  useEffect(() => {
    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set dimensions and margins
    const parentElement = parentRef.current;
    // const parentElement = document.getElementById(parentid);
    if (!parentElement) {
      // Handle the error
      console.error(`No element with ID ${parentid}`);
      return;
    }

    const newGroup: QuadrantDataItemProps[] = [];
    data.forEach((item) => {
      const exists: QuadrantDataItemProps = newGroup.find(
        (d: any) => d.X === item.X && d.Y === item.Y,
      ) as QuadrantDataItemProps;
      if (exists) {
        if (item.type.toLowerCase() === "user") {
          exists.users?.push(item.name);
        } else if (item.type.toLowerCase() === "entity") {
          exists.entities?.push({
            ip: item.ip,
            mac: item.mac,
            name: item.name,
          });
        }
      } else {
        const temp: any = { ...item };
        temp.users = [];
        temp.entities = [];
        if (item.type.toLowerCase() === "user") {
          temp.users.push(item.name);
        } else if (item.type.toLowerCase() === "entity") {
          temp.entities.push({
            ip: item.ip,
            mac: item.mac,
            name: item.name,
          });
        }
        newGroup.push(temp);
      }
    });

    const rect = (parentElement as HTMLElement).getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    let maxy = 0;
    let maxx = 0;

    for (const item of data) {
      if (item["X"] > maxy) {
        maxy = item["X"];
      }
      if (item["Y"] > maxx) {
        maxx = item["Y"];
      }
    }

    const uc = data.filter((item) => item.type.toLowerCase() === "user").length;
    setUserCount(uc);

    const ec = data.filter(
      (item) => item.type.toLowerCase() === "entity",
    ).length;
    setEntityCount(ec);

    // minimum and maximum values for the percentage and user count
    const percentages = {
      min: 0,
      max: maxy * 1,
    };

    const counts: { min: number; max: number } = {
      min: 0,
      max: maxx * 1,
    };

    const margin = {
      top: 20,
      right: 20,
      bottom: 50,
      left: 50,
    };

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr(
        "viewBox",
        `0 0 ${width + (margin.left + margin.right)} ${
          height + (margin.top + margin.bottom + 30)
        }`,
      );

    svg
      .append("clipPath")
      .attr("id", "clip")
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height);

    const group = svg
      .append("g")
      .attr("clip-path", "url(#clip)")
      .attr("class", "mainGroup")
      .attr("transform", `translate(${margin.left} ${margin.top})`);

    const freeGroup = svg
      .append("g")
      .attr("class", "freeGroup")
      .attr("transform", `translate(${margin.left} ${margin.top})`);

    const countScale = d3
      .scaleLinear()
      .domain(Object.values(counts))
      .range([0, width]);

    const percentageScale = d3
      .scaleLinear()
      .domain(Object.values(percentages))
      .range([height, 0]);

    const radiusRange = d3.scaleSqrt().range([0, 40]);
    radiusRange.domain([0, 40]);

    const quad = ["Crisis", "Act Now", "Beware", "Watch Out"];
    const quadcode = ["#C70039", "#F46034", "#57D68D", "#FFC300"];
    const quadIcons = [
      "/images/crisis.png",
      "/images/actnow.png",
      "/images/beware.png",
      "/images/watchout.png",
    ];

    const quadrantsGroup = group.append("g").attr("class", "quadrants");

    //Top Gradient
    const filter1 = svg
      .append("defs")
      .append("linearGradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "100%")
      .attr("y2", "0%")
      .attr("id", "topgrad");

    filter1
      .append("stop")
      .attr("offset", "0%")
      .style("stop-color", "#C70039")
      .style("stop-opacity", 1);

    filter1
      .append("stop")
      .attr("offset", "100%")
      .style("stop-color", "#F46034")
      .style("stop-opacity", 1);

    svg
      .insert("rect", ":first-child")
      .attr("width", "100%")
      .attr("height", "0.3rem")
      .attr("y", "0%")
      .attr("fill", "url(#topgrad)");
    //Top Gradient

    //Bottom Gradient
    const filter2 = svg
      .append("defs")
      .append("linearGradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "100%")
      .attr("y2", "0%")
      .attr("id", "bottomgrad");

    filter2
      .append("stop")
      .attr("offset", "0%")
      .style("stop-color", "#57D68D")
      .style("stop-opacity", 1);

    filter2
      .append("stop")
      .attr("offset", "100%")
      .style("stop-color", "#FFC300")
      .style("stop-opacity", 1);

    svg
      .insert("rect", ":first-child")
      .attr("width", "100%")
      .attr("height", "0.3rem")
      .attr("y", height + margin.bottom)
      .attr("fill", "url(#bottomgrad)");

    //Bottom Gradient

    //Axis Labels
    svg
      .append("text")
      .text("Anomaly Count")
      .attr("x", width)
      .style("text-anchor", "end")
      .attr("y", height + 90)
      .style("font-size", "1rem")
      .attr("fill", "var(--primary-foreground)")
      .style("font-weight", "bold");

    svg
      .append("text")
      .text("Threatscore")
      .attr("x", -40)
      .style("text-anchor", "end")
      .attr("y", 0)
      .attr("fill", "var(--primary-foreground)")
      .style("font-size", "1rem")
      .style("font-weight", "bold")
      .style("transform", "rotate(-90deg)");

    // include one group for each quadrant
    const quadrants = quadrantsGroup
      .selectAll("g.quadrant")
      .data(quad)
      .enter()
      .append("g")
      .attr("class", "quadrant")
      .attr(
        "transform",
        (_d, i) =>
          `translate(${i % 2 === 0 ? 0 : width / 2} ${i < 2 ? 0 : height / 2})`,
      );

    quadrants
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width / 2)
      .attr("height", height / 2)
      // include a darker shade for the third quadrant
      .attr("fill", "var(--secondary-threatscore-foreground)")
      // highlight the second and third quadrant with less transparency
      .attr("opacity", (_d, i) => (i === 1 || i === 2 ? 0.9 : 0.5));

    quadrants
      .append("text")
      .attr("x", width / 4)
      .attr("y", height / 4 + 25 * scale)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .text((d) => d)
      .style("text-transform", "uppercase")
      .style("font-size", "1rem")
      .attr("opacity", 0.9)
      .style("fill", "var(--foreground)")
      .style("font-weight", "600")
      .style("fill", (_d, i) => quadcode[i]);

    quadrants
      .append("image")
      .attr("xlink:href", (_d, i) => process.env.STATIC_PATH + quadIcons[i])
      .attr("x", width / 4)
      .attr("y", height / 4 - 20 * scale)
      .attr("width", "2rem")
      .attr("height", "2rem")
      .style("transform", "translate(-1rem, 0)");

    // axes
    let countAxis: any = d3.axisBottom(countScale);
    if (maxx <= 10) {
      countAxis = d3
        .axisBottom(countScale)
        .tickValues(d3.range(Math.floor(0), Math.ceil(maxx) + 1))
        .tickFormat((d) => d.toString());
    } else {
      countAxis = d3
        .axisBottom(countScale)
        .ticks(10, "d")
        .tickFormat((d) => d.toString());
    }
    const percentageAxis = d3
      .axisLeft(percentageScale)
      .tickFormat((d) => `${d}`);

    // add classes to later identify the axes individually and jointly
    const xAxisGroup = freeGroup
      .append("g")
      .attr("transform", `translate(0 ${height})`)
      .attr("class", "axis axis-count")
      .call(countAxis);

    const yAxisGroup = freeGroup
      .append("g")
      .attr("class", "axis axis-percentage")
      .call(percentageAxis);

    // Define zoom behavior
    const zoom: any = d3
      .zoom()
      .scaleExtent([1, 10])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .extent([
        [0, 0],
        [width, height],
      ])
      .on("zoom", zoomed);

    let currentTransform = d3.zoomIdentity;
    // Zoom function
    function zoomed(event: any) {
      // Create new scales based on zoom
      const newX = event.transform.rescaleX(countScale);
      const newY = event.transform.rescaleY(percentageScale);

      const [MinX, MaxX] = newX.domain();

      currentTransform = event.transform;
      // Update axes with new scales
      xAxisGroup.call(
        countAxis
          .tickValues(
            d3.range(
              Math.ceil(MinX),
              Math.floor(MaxX) + 1,
              Math.floor(MaxX / 10),
            ),
          )
          .scale(newX),
      );
      yAxisGroup.call(percentageAxis.scale(newY));
      dataPointsGroup.attr(
        "transform",
        (d) => `translate(${newX(d.Y)} ${newY(d.X)})`,
      );
      quadrantsGroup.attr(
        "transform",
        `translate(${event.transform.x}, ${event.transform.y}), scale(${event.transform.k})`,
      );
    }

    group.call(zoom);

    // remove the path describing the axes
    // d3.selectAll(".axis").select("path").remove();

    // style the ticks to be shorter
    d3.select(".axis-count").selectAll("line").attr("y2", 5);

    d3.select(".axis-percentage").selectAll("line").attr("x2", -4);

    d3.selectAll(".axis")
      .selectAll("text")
      .attr("font-size", "0.7rem")
      .style("fill", "var(--foreground)");

    // grid
    // include dotted lines for each tick and for both axes
    d3.select(".axis-count")
      .selectAll("g.tick")
      .append("path")
      .attr("d", `M 0 0 v -${height}`)
      .attr("stroke", "currentColor")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "2")
      .attr("opacity", 0);

    d3.select(".axis-percentage")
      .selectAll("g.tick")
      .append("path")
      .attr("d", `M 0 0 h ${width}`)
      .attr("stroke", "currentColor")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "2")
      .attr("opacity", 0);

    d3.select(".axis-count")
      .append("g")
      .attr("class", "label label-count")
      .attr("transform", `translate(${width / 2} ${margin.bottom})`);

    d3.select("g.label-count")
      .append("text")
      .attr("x", 0)
      .attr("y", -10)
      .text(XLabel ? XLabel : "")
      .attr("text-anchor", "middle");

    d3.select(".axis-percentage")
      .append("g")
      .attr("class", "label label-percentage")
      .attr("transform", `translate(-${margin.left} ${height / 2})`);

    d3.select("g.label-percentage")
      .append("text")
      .attr("x", 0)
      .attr("y", 0)
      .text(YLabel ? YLabel : "")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "hanging")
      .attr("transform", "rotate(-90)")
      .style("fill", "var(--foreground)");

    // style both labels with a heavier weight
    d3.selectAll("g.label text")
      .style("font-size", "1rem")
      .style("font-weight", "600")
      .style("letter-spacing", "0.05rem")
      .style("fill", "var(--foreground)");

    // data points
    // add a group for each data point, to group circle and text elements
    const dataGroup = group
      .append("g")
      .attr("clip-path", "url(#clip)")
      .attr("class", "data");

    const infoGroup = freeGroup
      .append("g")
      .style("opacity", 0)
      .attr("class", "iclass infoGroup")
      .style("pointer-events", "none")
      .style("cursor", "pointer");

    const dataPointsGroup = dataGroup
      .selectAll("g.data-point")
      .data(newGroup)
      .enter()
      .append("g")
      .attr("class", "data-point")
      .attr(
        "transform",
        ({ Y, X }) => `translate(${countScale(Y)} ${percentageScale(X)})`,
      )
      .style("cursor", "pointer")
      .on("click", function (_, i: any) {
        if (handleClick) {
          handleClick(i);
        }
      });

    // Add a larger invisible hit area so hover works reliably on each point.
    dataPointsGroup
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", Math.max(16, 14 * scale))
      .attr("fill", "#ffffff")
      .style("opacity", 0.001)
      .style("cursor", "pointer")
      .style("pointer-events", "all");

    // circles using the defined color
    dataPointsGroup
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", 5 * scale)
      .attr("fill", (d: QuadrantDataItemProps) =>
        d.type == "User"
          ? "var(--threatScore-orange)"
          : "var(--threatScore-blue)",
      )
      .style("transform", "scale(5)")
      .style("opacity", 0)
      .transition()
      .duration(1000)
      .delay(function (_d, i) {
        return i * 10;
      })
      .style("transform", "scale(1)")
      .style("opacity", 1)
      .style("cursor", "pointer")
      .style("pointer-events", "none");

    // labels describing the circle elements
    const dashLines = infoGroup
      .append("g")
      .attr("fill", "none")
      .attr("stroke", "var(--foreground)")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "7 4")
      .style("animation", "dashOffset 1.5s linear infinite");

    dashLines.append("path").attr("class", "vline").attr("d", "");

    dashLines.append("path").attr("class", "hline").attr("d", "");

    infoGroup
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("fill", "hsl(0, 0%, 0%)")
      .attr("stroke", "hsl(227, 9%, 81%)")
      .attr("stroke-width", 2)
      .style("pointer-events", "none")
      .attr("r", 0)
      .transition()
      .attr("r", 9.5);

    infoGroup.append("rect").attr("class", "bottomRect");

    infoGroup.append("rect").attr("class", "leftRect");

    infoGroup
      .append("text")
      .attr("class", "bottomText")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .style("fill", "var(--background)")
      .style("font-size", "0.8rem")
      .attr("x", 0)
      .attr("y", -7);

    infoGroup
      .append("text")
      .attr("class", "leftText")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .style("fill", "var(--background)")
      .style("font-size", "0.8rem")
      .attr("x", 12)
      .attr("y", 0);

    infoGroup.on("click", function () {
      const mac = d3.select(this).attr("data-mac");
      const ip = d3.select(this).attr("data-ip");
      const name = d3.select(this).attr("data-name");

      if (mac) {
        setMacAddress(mac);
      }
      if (ip) {
        setIpAddress(ip);
      }
      if (name) {
        setUserName(name);
      }
    });

    let hideTimeout: any = null;
    // Helper function to hide tooltip
    const hideInfo = () => {
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        const infoDiv: any = infoRef.current;
        if (infoDiv) {
          const info = d3.select(infoDiv);
          info
            .interrupt()
            .transition()
            .duration(200)
            .style("opacity", 0)
            .on("end", () => {
              info.style("pointer-events", "none");
            });
          infoDiv.classList.remove("opacity-100");
        }
      }, 80);
    };

    // on hover highlight the data point
    const showPointInfo = function (
      this: SVGGElement,
      event: any,
      d: QuadrantDataItemProps,
    ) {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      d3.select(this).raise();
      const infoDiv: any = infoRef.current;
      if (!infoDiv) return;
      d3.select(infoDiv).interrupt();
      // Set max-width for tooltip to enable wrapping
      infoDiv.style.maxWidth = "400px";
      infoDiv.style.minWidth = "200px";
      infoDiv.style.width = "auto";

      // STEP 1: Prepare and set all content first
      const entities = d.entities;
      const users = d.users;

      let entityHtml = "";
      if (entities && entities.length > 1) {
        entityHtml = `<div class="entities">
            <ul class="entitylist ps-4 list-disc">
              ${entities.map((e: any) => `<li onclick="open_entity('${e.mac}', '${e.ip}')" data-ip="${e.ip}" data-mac="${e.mac}" class="cursor-pointer infoItem" style="word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; white-space: normal;">${e.name}</li>`).join("")}
            </ul>
          </div>`;
      } else if (entities && entities.length === 1) {
        entityHtml = `<div onclick="open_entity('${entities[0].mac}', '${entities[0].ip}')" data-ip="${entities[0].ip}" data-mac="${entities[0].mac}" class="cursor-pointer infoItem" style="word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; white-space: normal;">${entities[0].name}</div>`;
      }

      let userHtml = "";
      if (users && users.length > 1) {
        userHtml = `<div class="users">
            <ul class="userlist ps-4 list-disc">
              ${users.map((u: string) => `<li onclick="open_user('${u}')" data-user="${u}" class="cursor-pointer infoItem" style="word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; white-space: normal;">${u}</li>`).join("")}
            </ul>
          </div>`;
      } else if (users && users.length === 1) {
        userHtml = `<div onclick="open_user('${users[0]}')" data-user="${users[0]}" class="cursor-pointer infoItem" style="word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; white-space: normal;">${users[0]}</div>`;
      }

      // Update hostGroup2 visibility
      if (userHtml !== "" && entityHtml !== "") {
        infoDiv.querySelector(".hostGroup2").classList.remove("hidden");
        infoDiv.querySelector(".hostGroup2").classList.add("flex");
        infoDiv.querySelector(".hostGroup2").classList.add("border-secondary");
        infoDiv.querySelector(".hostGroup2").classList.add("border-t");
        infoDiv.querySelector(".hostGroup2").classList.add("border-b");
        infoDiv.querySelector(".hostGroup2").classList.add("py-1");
      } else {
        infoDiv.querySelector(".hostGroup2").classList.add("hidden");
        infoDiv.querySelector(".hostGroup2").classList.remove("flex");
        infoDiv
          .querySelector(".hostGroup2")
          .classList.remove("border-secondary");
        infoDiv.querySelector(".hostGroup2").classList.remove("border-t");
        infoDiv.querySelector(".hostGroup2").classList.remove("border-b");
        infoDiv.querySelector(".hostGroup2").classList.remove("py-1");
      }

      const host1Title =
        userHtml != "" && entityHtml != ""
          ? "Hostname"
          : d.type.toLowerCase() == "user"
            ? "Username"
            : "Hostname";
      const host2Title = userHtml != "" && entityHtml != "" ? "Username" : "";

      const host1Type =
        userHtml != "" && entityHtml != ""
          ? "Entity"
          : d.type.toLowerCase() == "user"
            ? "User"
            : "Entity";
      const host2Type = userHtml != "" && entityHtml != "" ? "User" : "";

      infoDiv.querySelector(".hosttitle").textContent = host1Title;
      infoDiv.querySelector(".hosttitle2").textContent = host2Title;

      infoDiv.querySelector(".hostname").innerHTML =
        userHtml && entityHtml
          ? entityHtml
          : d.type.toLowerCase() == "user"
            ? userHtml
            : entityHtml;
      infoDiv.querySelector(".hostname2").innerHTML = userHtml;

      infoDiv.querySelector(".type").textContent = host1Type;
      infoDiv.querySelector(".type2").textContent = host2Type;
      infoDiv.querySelector(".score").textContent = `${d.count}`;
      infoDiv.querySelector(".count").textContent = `${d.score}`;

      // Force a reflow before measuring to get accurate dimensions
      void infoDiv.offsetWidth;
      const info = d3.select(infoDiv);
      const rect = (parentElement as HTMLElement).getBoundingClientRect();
      const tooltipRect = infoDiv.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      const padding = 10;
      const cursorGap = 16;
      const [mouseX, mouseY] = d3.pointer(event, parentElement as Element);
      let posX = mouseX + cursorGap;
      let posY = mouseY - tooltipHeight / 2;

      // Flip to the left side when the tooltip would overflow on the right.
      if (posX + tooltipWidth + padding > rect.width) {
        posX = mouseX - tooltipWidth - cursorGap;
      }

      // Clamp horizontally after flip to keep tooltip fully inside chart.
      if (posX < padding) posX = padding;
      if (posX + tooltipWidth > rect.width - padding)
        posX = rect.width - tooltipWidth - padding;

      // Clamp vertically if it goes out of top/bottom bounds
      if (posY < padding) posY = padding;
      if (posY + tooltipHeight > rect.height - padding)
        posY = rect.height - tooltipHeight - padding;

      info
        .style("transform", `translate(${posX}px, ${posY}px)`)
        .style("opacity", 1)
        .style("pointer-events", "auto");

      infoDiv.classList.add("opacity-100");

      info.selectAll(".infoItem").on("click", function (event: any) {
        const mac = event.target.getAttribute("data-mac");
        const ip = event.target.getAttribute("data-ip");
        const user = event.target.getAttribute("data-user");
        if (mac) {
          setMacAddress(mac);
        }
        if (ip) {
          setIpAddress(ip);
        }
        if (user) {
          setUserName(user);
        }
        hideInfo();
      });

      const hostname: any = d3.select("text.hostname");
      const atype: any = d3.select("text.type");
      const score = d3.select("text.score");
      const count = d3.select("text.count");

      hostname.text(
        `${d.type.toLowerCase() == "user" ? "Username" : "Hostname"}: ${d.name}`,
      );
      atype.text(`Type: ${d.type}`);
      score.text(`ThreatScore: ${d.count}`);
      count.text(`Count: ${d.score}`);

      const el = d3.select(this);
      infoGroup.style("pointer-events", "none");
      infoGroup.transition().duration(200).style("opacity", 1);

      const elNode = el?.node();
      infoGroup.attr(
        "transform",
        `translate(${elNode?.transform.baseVal[0].matrix.e}, ${elNode?.transform.baseVal[0].matrix.f})`,
      );

      const newX = currentTransform.rescaleX(countScale);
      const newY = currentTransform.rescaleY(percentageScale);

      el.select("circle").raise();

      dashLines.select(".vline").attr("d", () => {
        return `M 0 0 v ${height + newY(newY.domain()[1]) - newY(d.X)}`;
      });

      dashLines.select(".hline").attr("d", () => `M 0 0 h -${newX(d.Y)}`);

      const textCount = infoGroup
        .select(".bottomText")
        .attr("x", 0)
        .attr("y", -10)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("fill", "var(--background)")
        .text(() => d.Y)
        .attr(
          "transform",
          () => `translate(0 ${height + newY(newY.domain()[1]) - newY(d.X)})`,
        );

      const textPercentage = infoGroup
        .select(".leftText")
        .attr("x", 12)
        .attr("y", 0)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("fill", "var(--background)")
        .text(() => `${d.X}`)
        .attr("transform", () => `translate(-${newX(d.Y)} 0)`);

      const textECount = textCount.node() as SVGTextElement;
      const { width: countWidth, height: countHeight } = textECount.getBBox();

      const textPCount = textPercentage.node() as SVGTextElement;
      const { width: percentageWidth, height: percentageHeight } =
        textPCount.getBBox();

      infoGroup
        .select(".bottomRect")
        .attr("x", -countWidth / 2 - 4)
        .attr("y", -countHeight / 2 - 12)
        .attr("width", countWidth + 8)
        .attr("height", countHeight + 4)
        .style("fill", "var(--foreground)")
        .attr("rx", 2)
        .attr(
          "transform",
          () => `translate(0 ${height + newY(newY.domain()[1]) - newY(d.X)})`,
        );

      infoGroup
        .select(".leftRect")
        .attr("x", -percentageWidth / 2 + 8)
        .attr("y", -percentageHeight / 2 - 2)
        .attr("width", percentageWidth + 8)
        .attr("height", percentageHeight + 4)
        .style("fill", "var(--foreground)")
        .attr("rx", 2)
        .attr("transform", () => `translate(-${newX(d.Y)} 0)`);
    };

    dataPointsGroup
      .on("mouseenter", showPointInfo)
      .on("mousemove", showPointInfo);
    // .on("mouseleave", function () {
    //   hideInfo();
    // });
  }, [data]);

  function showInfo() {
    const infoDiv: any = infoRef.current;
    if (infoDiv) {
      const info = d3.select(infoDiv);
      info.style("pointer-events", "auto");
      info.transition().duration(500).style("opacity", 1);
      d3.select(".infoGroup").transition().duration(200).style("opacity", 1);
    }
  }

  // function hideInfo() {
  //   const infoDiv: any = infoRef.current;
  //   if (infoDiv) {
  //     const info = d3.select(infoDiv);
  //     info.style("pointer-events", "none");
  //     info.transition().duration(500).style("opacity", 0);
  //     d3.select(".infoGroup").transition().duration(200).style("opacity", 0);
  //   }
  // }

  return (
    <>
      {(macAddress != "" || ipAddress != "") &&
        isFeatureAllowed(ALL_KEYS.ASSET_ENTITY_INSPECTOR) && (
          <AssetEntityInspectorContent
            mac={macAddress}
            open={true}
            ipAddress={ipAddress}
            onClose={() => {
              setMacAddress("");
              setIpAddress("");
            }}
          />
        )}

      {/* {(macAddress != "" || ipAddress != "") &&
        isFeatureAllowed(ALL_KEYS.ENTITY_INSPECTOR) && (
          <EntityInspector
            mac={macAddress}
            open={true}
            onClose={() => {
              setMacAddress("");
              setIpAddress("");
            }}
          />
        )} */}

      {userName != "" &&
        isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_ACCESS) && (
          <UserInvestigator
            account={userName}
            openState={true}
            onClose={() => {
              setUserName("");
            }}
          />
        )}

      {/* {userName != "" && isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_ST) && (
        // <DDRUserInvestigator
        //   userId={userName}
        //   isOpen={true}
        //   onClose={() => {
        //     setUserName("");
        //   }}
        // />
        <UserInvestigator
          account={userName}
          openState={true}
          onClose={() => {
            setUserName("");
          }}
        />
      )} */}
      <div className="h-full w-full flex flex-col relative">
        <div
          onMouseEnter={() => showInfo()}
          // onMouseLeave={() => hideInfo()}
          style={{ pointerEvents: "none", opacity: 0 }}
          className="absolute top-0 left-0 pt-4 ps-6"
          ref={infoRef}
        >
          <div className="rounded-md bg-foreground text-background text-xxs py-2 px-3 flex flex-col gap-1">
            <div className="hostGroup flex gap-1 flex-col">
              <div className="flex gap-1">
                <div className="font-bold text-secondary hosttitle">
                  Hostname:
                </div>
                <div className="hostname"></div>
              </div>
              <div className="flex gap-1">
                <div className="font-bold text-secondary">Type:</div>
                <div className="type"></div>
              </div>
            </div>

            <div className="hostGroup2 hidden gap-1 flex-col">
              <div className="flex gap-1">
                <div className="font-bold text-secondary hosttitle2">
                  Hostname:
                </div>
                <div className="hostname2"></div>
              </div>
              <div className="flex gap-1">
                <div className="font-bold text-secondary">Type:</div>
                <div className="type2"></div>
              </div>
            </div>

            <div className="flex gap-1">
              <div className="font-bold text-secondary">Threatscore:</div>
              <div className="score"></div>
            </div>
            <div className="flex gap-1">
              <div className="font-bold text-secondary">Count:</div>
              <div className="count"></div>
            </div>
          </div>
        </div>
        <div className="flex-1" ref={parentRef}>
          <svg ref={svgRef}></svg>
        </div>
        <div className="flex justify-center gap-4 pb-4">
          {entityCount > 0 && (
            <div className="flex gap-2 items-center">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: "var(--threatScore-blue)" }}
              ></div>
              <div className="text-sm">Entity :{entityCount}</div>
            </div>
          )}
          {userCount > 0 && (
            <div className="flex gap-2 items-center">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: "var(--threatScore-orange)" }}
              ></div>
              <div className="text-sm">User :{userCount}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuadrantChart;
